import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link2, Unlink, Plus, Search, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/api/client';

interface Member {
  id: number; companyName: string; brandName?: string | null; email: string;
  status: string; serviceType?: string | null; walletBalance: number; isSelf: boolean;
}
interface Group { groupId: number | null; groupName: string | null; members: Member[] }
interface Candidate { id: number; companyName: string; brandName?: string | null; email: string; status: string }

/**
 * Link a merchant's other accounts so its operator can switch between them without a second login.
 *
 * The warning on this screen is not decoration. Linking is SYMMETRIC and passwordless: once two
 * accounts share a group, whoever can sign into either one can read both companies' orders, wallets
 * and invoices. That is correct when one owner really runs both businesses and a serious leak when
 * they do not — so the screen says so plainly at the point of decision, not in a manual.
 */
export function MerchantLinkedAccountsTab({ merchantId }: { merchantId: number }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<number[]>([]);

  const groupQ = useQuery({
    queryKey: ['linked-accounts', merchantId],
    queryFn: async () => (await api.get<Group>(`/merchants/${merchantId}/linked-accounts`)).data,
  });
  const candQ = useQuery({
    queryKey: ['linked-candidates', merchantId, search],
    queryFn: async () => (await api.get<Candidate[]>(`/merchants/${merchantId}/linked-accounts/candidates`, { params: { search } })).data,
    enabled: search.trim().length >= 2,
  });
  const done = (msg: string) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ['linked-accounts', merchantId] });
    qc.invalidateQueries({ queryKey: ['linked-candidates', merchantId] });
    setPicked([]); setSearch('');
  };
  const fail = (e: any) => toast.error(e?.response?.data?.message ?? 'That did not work');

  const linkMut = useMutation({
    mutationFn: async (ids: number[]) => (await api.post(`/merchants/${merchantId}/linked-accounts`, { merchantIds: ids })).data,
    onSuccess: () => done('Accounts linked'), onError: fail,
  });
  const unlinkMut = useMutation({
    mutationFn: async (otherId: number) => (await api.delete(`/merchants/${merchantId}/linked-accounts/${otherId}`)).data,
    onSuccess: () => done('Account unlinked'), onError: fail,
  });
  const unlinkAllMut = useMutation({
    mutationFn: async () => (await api.delete(`/merchants/${merchantId}/linked-accounts/all`)).data,
    onSuccess: () => done('Group dissolved'), onError: fail,
  });

  const group = groupQ.data;
  const members = group?.members ?? [];

  return (
    <div className="p-5 space-y-5 max-w-4xl">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Link2 size={20} className="text-emerald-700" />
          <h3 className="text-lg font-semibold">Linked Accounts</h3>
        </div>
        <p className="text-sm text-slate-600">
          Other merchant accounts owned by the same business. Their operator can switch between them from
          the portal without signing in again.
        </p>
      </div>

      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <div>
          <p><span className="font-semibold">Linking works both ways and needs no password.</span> Once these
          accounts are linked, anyone who can sign into <em>any one</em> of them can open the others and see their
          orders, wallet and invoices.</p>
          <p className="mt-1">Only link accounts you have confirmed belong to the same owner.</p>
        </div>
      </div>

      <div className="bg-white border rounded-xl">
        <div className="px-4 py-2.5 border-b flex items-center justify-between">
          <span className="text-sm font-medium">
            {members.length ? `${group?.groupName ?? 'Group'} · ${members.length} accounts` : 'Not linked to any account'}
          </span>
          {members.length > 0 && (
            <button onClick={() => { if (confirm('Unlink every account in this group? Nobody will be able to switch between them.')) unlinkAllMut.mutate(); }}
              className="text-xs text-rose-600 hover:underline">Dissolve group</button>
          )}
        </div>
        {groupQ.isLoading ? (
          <div className="p-6 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline" /></div>
        ) : !members.length ? (
          <div className="p-6 text-center text-sm text-slate-400">
            This account stands alone. Search below to link its sister companies.
          </div>
        ) : (
          <ul className="divide-y">
            {members.map((m) => (
              <li key={m.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {m.brandName || m.companyName}
                    {m.isSelf && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">this account</span>}
                    {m.status !== 'ACTIVE' && m.status !== 'APPROVED' && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">{m.status} — cannot be opened</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">#{m.id} · {m.email}</div>
                </div>
                {!m.isSelf && (
                  <button onClick={() => { if (confirm(`Unlink ${m.brandName || m.companyName}?`)) unlinkMut.mutate(m.id); }}
                    className="text-xs px-2 py-1 border border-rose-200 text-rose-600 rounded hover:bg-rose-50 inline-flex items-center gap-1 shrink-0">
                    <Unlink size={12} /> Unlink
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border rounded-xl p-4">
        <div className="text-sm font-medium mb-2">Link another account</div>
        <div className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 mb-2 max-w-md">
          <Search size={14} className="text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company name or email (2+ characters)"
            className="px-1 py-2 text-sm outline-none flex-1" />
        </div>
        {search.trim().length >= 2 && (
          <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
            {candQ.isLoading && <div className="p-3 text-center text-slate-400 text-sm"><Loader2 size={14} className="animate-spin inline" /></div>}
            {!candQ.isLoading && !(candQ.data ?? []).length && <div className="p-3 text-sm text-slate-400 text-center">No matching accounts.</div>}
            {(candQ.data ?? []).map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={picked.includes(c.id)}
                  onChange={(e) => setPicked((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))} />
                <span className="min-w-0">
                  <span className="block text-sm truncate">{c.brandName || c.companyName}</span>
                  <span className="block text-xs text-slate-500 truncate">#{c.id} · {c.email} · {c.status}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <button onClick={() => linkMut.mutate(picked)} disabled={!picked.length || linkMut.isPending}
          className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm inline-flex items-center gap-1 disabled:opacity-50">
          {linkMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Link {picked.length ? `${picked.length} account${picked.length > 1 ? 's' : ''}` : ''}
        </button>
      </div>

    </div>
  );
}
